import React from "react";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import { Box, Grid, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { Link as LinkGatsby } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import SocialMediaIcons from "../svgs/SocialMediaIcons";
import ContainerBlock from "../components/ContainerBlock";

const footerLinks = {
  products: [
    {
      name: "Autosocializer",
      href: "https://www.autosocializer.com",
      target: "_blank",
    },
  ],

  company: [
    // {
    // 	name: "Get Quote",
    // 	href: "/get-quote",
    // 	target: "_self",
    // },
    {
      name: "Contact Us",
      href: "/contact-us",
      target: "_self",
    },
    {
      name: "Careers",
      href: "/careers",
      target: "_self",
    },
    {
      name: "Events",
      href: "/events",
      target: "_self",
    },
    {
      name: "Blogs",
      href: "/blogs",
      target: "_self",
    },
  ],

  developers: [
    {
      name: "Site Audit CLI",
      href: "https://www.npmjs.com/package/@xtellig/site-audit",
      target: "_blank",
      tooltip: "@xtellig/site-audit",
    },
  ],
};

const contact_details = [
  {
    details: "hello@xtellig.com",
    logo: <EmailOutlinedIcon />,
  },
  {
    details: "+94 77 425 0532",
    logo: <LocalPhoneOutlinedIcon />,
  },
  {
    details: "Vavuniya, Sri Lanka.",
    logo: <LocationOnOutlinedIcon />,
  },
];

const Footer = () => {
  const theme = useTheme();

  const getCurrentYear = (): number => new Date().getFullYear();

  return (
    <ContainerBlock
      sx={{
        position: "relative",
        px: { xs: "20px", sm: 2, md: 4 },
        pt: { xs: "48px", md: 10 },
        pb: { xs: 4, md: 4 },
        borderTop: "1px solid #EFF0F6 ",
      }}
    >
      <Grid container spacing={4} justifyContent="space-between">
        <Grid
          size={{ xs: 12, lg: 4 }}
          display={"flex"}
          flexDirection={"column"}
          gap={"24px"}
          alignItems={"flex-start"}
          borderBottom={{ xs: "1px solid #D9DBE9", md: "none" }}
          pb={"32px"}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <LinkGatsby to="/" style={{ display: "inline-block" }} title="Logo">
              <StaticImage
                src="../../../static/logos/logo-dt.png"
                placeholder="blurred"
                loading="lazy"
                alt="Xtellig Logo"
                style={{
                  height: "33px",
                  width: "auto",
                  display: "flex",
                  backgroundColor: "transparent",
                }}
                imgStyle={{
                  objectFit: "contain",
                }}
              />
            </LinkGatsby>
            <Box sx={{ display: { xs: "block", md: "none" } }}>
              <SocialMediaIcons />
            </Box>
          </Box>

          <Typography
            sx={{
              color: "#858C95",
              fontSize: "1rem",
              lineHeight: 1.6,
              textAlign: "left",
            }}
          >
            Delivering outstanding products and services to transform and
            enhance customers' lives.
          </Typography>
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <SocialMediaIcons />
          </Box>
        </Grid>

        <Grid container size={{ xs: 12, lg: 8 }} spacing={{xs:4,md:2}}>
          {Object.entries(footerLinks).map(([section, links]) => (
            <Grid
              key={section}
              size={{ xs: 12, sm: 3 }}
              display="flex"
              flexDirection="column"
              gap={2}
              sx={{
                alignItems: "flex-start",
                textAlign: "left",
              }}
            >
              <Typography
                sx={{ color: "#323539", fontWeight: 600, fontSize: "1rem" }}
              >
                {section === "legal"
                  ? "Legal & Policies"
                  : section.charAt(0).toUpperCase() + section.slice(1)}
              </Typography>

              <Stack spacing={{xs:"8px"}}>
                {links.map((link: any) => {
                  const linkElement = (
                    <LinkGatsby
                      key={link.name}
                      title={link.name}
                      to={link.href}
                      target={link.target}
                      rel={
                        link.target === "_blank"
                          ? "noopener noreferrer"
                          : undefined
                      }
                      partiallyActive
                      activeStyle={{ color: theme.palette.primary.main }}
                      style={{
                        textDecoration: "none",
                        color: theme.palette.grey[600],
                        fontSize: "1rem",
                      }}
                    >
                      <Box
                        sx={{
                          display: "inline-block",
                          "&:hover": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      >
                        {link.name}
                      </Box>
                    </LinkGatsby>
                  );

                  return link.tooltip ? (
                    <Tooltip
                      key={link.name}
                      title={link.tooltip}
                      arrow
                      placement="top"
                    >
                      <Box display="inline-flex">{linkElement}</Box>
                    </Tooltip>
                  ) : (
                    linkElement
                  );
                })}
              </Stack>
            </Grid>
          ))}

          <Grid
            size={{ xs: 12, sm: 3 }}
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ alignItems: "flex-start" }}
          >
            <Typography
              sx={{ color: "#323539", fontWeight: 600, fontSize: "1rem" }}
            >
              Contact
            </Typography>

            <Stack spacing={1}>
              {contact_details.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    color: "#858C95",
                    fontSize: "1rem",
                  }}
                >
                  <Box sx={{ mt: 0.3 }}>{item.logo}</Box>

                  {item.logo.type === EmailOutlinedIcon ? (
                    <a
                      href={`mailto:${item.details}`}
                      style={{ color: "#858C95", textDecoration: "none" }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {item.details}
                    </a>
                  ) : item.logo.type === LocalPhoneOutlinedIcon ? (
                    <a
                      href={`tel:${item.details.replace(/\s/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#858C95", textDecoration: "none" }}
                    >
                      {item.details}
                    </a>
                  ) : (
                    <Typography sx={{ color: "#858C95", fontSize: "1rem" }}>
                      {item.details}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Grid>

      <Box
        sx={{
          display: { xs: "none", md: "block" },
          mt: { xs: 5, md: 8 },
          width: "100%",
          overflow: "hidden",
        }}
      >
        <StaticImage
          src="../../../static/logos/footer-logo.png"
          alt="Xtellig"
          placeholder="none"
          loading="lazy"
          style={{
            width: "100%",
            display: "block",
          }}
        />
      </Box>

      {/* Copyright Bar */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems="center"
        sx={{
          borderTop: "1px solid rgba(133,140,149,0.2)",
          pt: { xs: 4, md: 3 },
          mt: 3,
        }}
      >
        <Typography
          sx={{
            color: "#858C95",
            fontSize: "1rem",
            order: { xs: 2, md: 1 },
            flexShrink: 0,
          }}
        >
          © {getCurrentYear()} Xtellig Pvt Ltd. All rights reserved.
        </Typography>

        <LinkGatsby
          to="/privacy-policy/"
          title="Privacy Policy"
          partiallyActive
          activeStyle={{ color: theme.palette.primary.main }}
          style={{
            textDecoration: "none",
            color: theme.palette.grey[600],
            fontSize: "1rem",
            transition: "color 0.2s ease",
            order: 2,
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = theme.palette.primary.main;
          }}
          onMouseLeave={(e) => {
            if (window.location.pathname !== "/privacy-policy") {
              e.currentTarget.style.color = theme.palette.grey[600];
            }
          }}
        >
          <Box
            sx={{
              display: "inline-block",
              "&:hover": {
                color: theme.palette.primary.main,
              },
            }}
          >
            {" "}
            Privacy Policy
          </Box>
        </LinkGatsby>
      </Stack>
    </ContainerBlock>
  );
};

export default Footer;
